import { IconButton, makeStyles } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import Card from "@material-ui/core/Card";
import Typography from "@material-ui/core/Typography";
import { ArrowBack } from "@material-ui/icons";
import { useAssignInfo } from "@saleor/accountSettings/misc";
import CardSpacer from "@saleor/components/CardSpacer";
import Container from "@saleor/components/Container";
import Form from "@saleor/components/Form";
import PageHeaderSaleor from "@saleor/components/PageHeader";
import SingleAutocompleteSelectField, {
  SingleAutocompleteChoiceType
} from "@saleor/components/SingleAutocompleteSelectField";
import FilterTabs, { FilterTab } from "@saleor/components/TableFilter";
import { DEFAULT_INITIAL_PAGINATION_DATA } from "@saleor/config";
import useListSettings from "@saleor/hooks/useListSettings";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import {
  createPaginationState,
  PaginationState
} from "@saleor/hooks/usePaginator";
import useUser from "@saleor/hooks/useUser";
import { buttonMessages, sectionNames } from "@saleor/intl";
import { endDateTime, findInEnum, maybe, startDateTime } from "@saleor/misc";
import RemittanceRunnerMarkAsCollectedModal from "@saleor/remittanceRunner/components/RemittanceRunnerMarkAsCollectedModal/RemittanceRunnerMarkAsCollectedModal";
import RemittanceRunnerTableToRemit from "@saleor/remittanceRunner/components/RemittanceRunnerTableToRemit";
import { getFilterOptsForToRemit } from "@saleor/remittanceRunner/filters/filterOpts";
import { getFilterQueryParam } from "@saleor/remittanceRunner/filters/filterQueryParams";
import {
  useGenerateRemittanceId,
  useMarkRemittanceAsPaymentCollected
} from "@saleor/remittanceRunner/mutations";
import {
  convertStringToBoolean,
  getOrderManuallyAdjustedAmount,
  isInvalidThreeMonth,
  isOrderManuallyAdjusted,
  REMITTANCE_DEFAULT_DELIVERY_TYPES
} from "@saleor/remittanceRunner/utils/handlers";
import usePaginatorRemittance from "@saleor/remittanceRunner/utils/paginators";
import { ListViews } from "@saleor/types";
import {
  DeliveryTypeEnum,
  SmopOrderStatusEnum
} from "@saleor/types/globalTypes";
import { currencyFormat } from "@saleor/utils/formatters/currencyFormat";
import createFilterHandlers from "@saleor/utils/handlers/filterHandlers";
import createSingleAutocompleteSelectHandler from "@saleor/utils/handlers/singleAutocompleteSelectChangeHandler";
import * as React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import {
  useAccountAssignedMall,
  useRemittanceRunnerOrders
} from "../../queries";
import { useTableStyles } from "../../styles/tableStyles";
import {
  remittanceRunnerListUrl,
  RemittanceRunnerListUrlQueryParams,
  remittanceRunnerToRemitUrl
} from "../../url";
import { ToRemitFormData } from "../../utils/formDataTypes";

const useStyles = makeStyles(
  theme => ({
    actions: {
      alignItems: "center",
      display: "flex",
      marginTop: "20px"
    },
    btnRemit: {
      backgroundColor: "#0630ff",
      marginTop: "10px"
    },
    card: {
      padding: "20px 5px"
    },
    cardContainer: {
      display: "grid",
      gridColumnGap: theme.spacing(3),
      gridTemplateColumns: "1fr 1fr",
      [theme.breakpoints.down("sm")]: {
        gridColumnGap: theme.spacing(1)
      },
      [theme.breakpoints.down("xs")]: {
        gridTemplateColumns: "1fr"
      }
    },
    cardFooter: {
      display: "flex",
      justifyContent: "space-between"
    },
    content: {
      margin: "0px 50px"
    },
    leftSpacer: {
      marginLeft: "10px"
    },
    remittedAmount: {
      marginTop: "10px"
    }
  }),
  { name: "RemittanceRunnerToRemitPage" }
);

export interface RemittanceRunnerToRemitProps {
  params: RemittanceRunnerListUrlQueryParams;
}

const RemittanceRunnerToRemit: React.FC<RemittanceRunnerToRemitProps> = ({
  params
}) => {
  const classes = useStyles({});
  const tblClasses = useTableStyles({});
  const intl = useIntl();
  const navigate = useNavigator();
  const notify = useNotifier();
  const user = useUser();
  const userInfo = useAssignInfo(user?.user);

  const { updateListSettings, settings } = useListSettings(
    ListViews.REMITTANCE_LIST
  );
  const initialForm: ToRemitFormData = {
    paymentChannel: "BDO"
  };
  const paymentChoices: SingleAutocompleteChoiceType[] = [
    {
      label: "BDO",
      value: "BDO"
    }
  ];

  const [amountTotal, setAmountTotal] = React.useState(0);
  const [checkedOrders, setCheckedOrders] = React.useState([]);
  const [selectedPaymentChannel, setSelectedPaymentChannel] = React.useState(
    "BDO"
  );
  const [paymentChannelDisplay, setPaymentChannelDisplay] = React.useState(
    "BDO"
  );
  const [currentTab, setCurrentTab] = React.useState<number>(0);
  const [displayDialog, setDisplayDialog] = React.useState(false);

  const [searchQuery, setSearchQuery] = React.useState("");
  const [motherOrder, setMotherOrder] = React.useState(params?.motherOrder);
  const [dateShippedFrom, setDateShippedFrom] = React.useState(
    params?.dateShippedFrom
  );
  const [dateShippedTo, setDateShippedTo] = React.useState(
    params?.dateShippedTo
  );
  const [dateCompletedFrom, setDateCompletedFrom] = React.useState(
    params?.dateCompletedFrom
  );
  const [dateCompletedTo, setDateCompletedTo] = React.useState(
    params?.dateCompletedTo
  );
  const [riderFirstName, setRiderFirstName] = React.useState(
    params?.riderFirstName
  );
  const [riderLastName, setRiderLastName] = React.useState(
    params?.riderLastName
  );
  const [deliveryType, setDeliveryType] = React.useState(params?.deliveryType);
  const [amount, setAmount] = React.useState(params?.amount);
  const [isPaymentCollected, setIsPaymentCollected] = React.useState<any>(
    convertStringToBoolean(params?.isPaymentCollected)
  );
  const [checkedAll, setCheckedAll] = React.useState(false);

  const [queryVariables, setQueryVariables] = React.useState<PaginationState>({
    first: settings.rowNumber
  });

  const paginateRemittance = usePaginatorRemittance();
  const paginationState =
    createPaginationState(settings.rowNumber, params)?.first !== null
      ? createPaginationState(settings.rowNumber, params)
      : { first: settings.rowNumber };

  const filterOpts = getFilterOptsForToRemit(params);

  const [changeFilters, resetFilters] = createFilterHandlers({
    createUrl: remittanceRunnerToRemitUrl,
    getFilterQueryParam,
    navigate,
    params
  });

  React.useEffect(() => {
    setMotherOrder(params?.motherOrder);
    setDateShippedFrom(params?.dateShippedFrom);
    setDateShippedTo(params?.dateShippedTo);
    setDateCompletedFrom(params?.dateCompletedFrom);
    setDateCompletedTo(params?.dateCompletedTo);
    setRiderFirstName(params?.riderFirstName);
    setRiderLastName(params?.riderLastName);
    setDeliveryType(params?.deliveryType);
    setAmount(params?.amount);
    setIsPaymentCollected(convertStringToBoolean(params?.isPaymentCollected));
  }, [params]);

  React.useMemo(() => {
    setQueryVariables(paginationState);
  }, [params]);

  React.useEffect(() => {
    navigate(
      remittanceRunnerToRemitUrl({
        ...params,
        ...DEFAULT_INITIAL_PAGINATION_DATA
      }),
      true
    );
  }, [settings.rowNumber]);

  React.useEffect(() => {
    setAmountTotal(
      orderList
        .filter(order => checkedOrders.includes(order.id))
        .map(order =>
          isOrderManuallyAdjusted(order)
            ? getOrderManuallyAdjustedAmount(order)
            : order?.total?.gross?.amount
        )
        .reduce((a, b) => a + b, 0)
    );
  }, [checkedOrders]);

  React.useEffect(() => {
    if (isInvalidThreeMonth(params.dateCompletedFrom, params.dateCompletedTo)) {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage:
            "Unable to filter the report data. Please limit the date range to 3 months."
        })
      });
      navigate(remittanceRunnerToRemitUrl());
    }
  }, [params.dateCompletedFrom, params.dateCompletedTo]);

  React.useEffect(() => {
    if (isInvalidThreeMonth(params.dateShippedFrom, params.dateShippedTo)) {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage:
            "Unable to filter the report data. Please limit the date range to 3 months."
        })
      });
      navigate(remittanceRunnerToRemitUrl());
    }
  }, [params.dateShippedFrom, params.dateShippedTo]);

  const {
    data: orders,
    loading: loadingOrders,
    refetch
  } = useRemittanceRunnerOrders({
    variables: {
      ...queryVariables,
      amount,
      completedAt: {
        gte: dateCompletedFrom ? startDateTime(dateCompletedFrom) : null,
        lte: dateCompletedTo ? endDateTime(dateCompletedTo) : null
      },
      deliveryTypes: maybe(
        () => deliveryType.map(status => findInEnum(status, DeliveryTypeEnum)),
        REMITTANCE_DEFAULT_DELIVERY_TYPES
      ),
      isPaymentCollected,
      orderCode: motherOrder,
      paymentType: "cash",
      rider: {
        firstName: riderFirstName,
        lastName: riderLastName
      },
      search: searchQuery,
      shippedAt: {
        gte: dateShippedFrom ? startDateTime(dateShippedFrom) : null,
        lte: dateShippedTo ? endDateTime(dateShippedTo) : null
      },
      smopStatus: [SmopOrderStatusEnum.COMPLETED, SmopOrderStatusEnum.SHIPPED]
    }
  });

  const {
    loadNextPageRemittance,
    loadPreviousPageRemittance,
    pageInfoRemittance
  } = paginateRemittance(
    orders?.remittanceOrders?.pageInfo,
    paginationState,
    params
  );

  const orderList = maybe(
    () => orders.remittanceOrders.edges.map(edge => edge.node),
    []
  );

  const handleBack = () => navigate(remittanceRunnerListUrl());

  const handleChecked = (ev, order_id) => {
    const checked = ev.target.checked;

    if (checked && !checkedOrders.includes(order_id)) {
      setCheckedOrders(checkedOrders => [...checkedOrders, order_id]);
    } else if (checkedOrders.length > 0) {
      setCheckedOrders(checkedOrders.filter(i => i !== order_id));
    }
  };
  const handleCheckedAll = ev => {
    const checked = ev.target.checked;
    setCheckedAll(checked);

    if (checked) {
      setCheckedOrders(orderList.map(val => val.id));
    } else {
      setCheckedOrders([]);
    }
  };

  const [
    generateRemittanceId,
    generateRemittanceIdOpts
  ] = useGenerateRemittanceId({
    onCompleted: data => {
      if (data?.generateRemittanceId?.errors?.length === 0) {
        notify({
          status: "success",
          text:
            "Generated Remittance No " +
            data.generateRemittanceId.remittance.remittanceNo +
            "."
        });
        handleBack();
      } else if (data?.generateRemittanceId?.errors?.length > 0) {
        data.generateRemittanceId.errors.forEach(error => {
          notify({
            status: "error",
            text: error.message
          });
        });
      }
    }
  });

  const [
    markRemittanceAsPaymentCollected,
    markRemittanceAsPaymentCollectedOpts
  ] = useMarkRemittanceAsPaymentCollected({
    onCompleted: data => {
      if (data?.markRemittanceAsPaymentCollected?.errors?.length === 0) {
        notify({
          status: "success",
          text: "Marked as Collected"
        });
        resetFilters();
        setIsPaymentCollected(null);
        setCheckedOrders([]);
        setCheckedAll(false);
        setCurrentTab(0);
        setDisplayDialog(false);
      } else if (data?.markRemittanceAsPaymentCollected?.errors?.length > 0) {
        data.markRemittanceAsPaymentCollected.errors.forEach(error => {
          notify({
            status: "error",
            text: error.message
          });
        });
      }
    }
  });

  const handleSave = (forPaymentCollection: boolean) => {
    if (checkedOrders.length === 0) {
      notify({
        status: "error",
        text: "Please select mother order/s before saving"
      });
    } else if (forPaymentCollection) {
      markRemittanceAsPaymentCollected({
        variables: {
          ids: checkedOrders,
          isPaymentCollected: true
        }
      });
    } else {
      generateRemittanceId({
        variables: {
          ids: checkedOrders
        }
      });
    }
  };

  const tabChanged = tabIndex => {
    setCurrentTab(tabIndex);
    setIsPaymentCollected(tabIndex === 1 ? false : null);
    setCheckedOrders([]);
    setCheckedAll(false);
    resetFilters();

    navigate(
      remittanceRunnerToRemitUrl({
        isPaymentCollected: tabIndex === 1 ? false : null,
        ...{},
        ...DEFAULT_INITIAL_PAGINATION_DATA
      }),
      true
    );
  };

  const { data: userAssignedMall } = useAccountAssignedMall({
    displayLoader: true,
    skip: !userInfo?.mall,
    variables: {
      id: userInfo?.mall
    }
  });
  const minRemittanceLimit =
    userAssignedMall?.mall?.remittanceConfig?.minRemittanceLimit || 0;
  const compareMinToCurrent = minRemittanceLimit > amountTotal;

  return (
    <Container>
      <Card className={classes.card}>
        <PageHeaderSaleor
          title={
            <span>
              <IconButton aria-label="back" onClick={handleBack}>
                <ArrowBack color="primary" />
              </IconButton>
              {intl.formatMessage(sectionNames.remittanceRunnerToRemit)}
            </span>
          }
        ></PageHeaderSaleor>

        <Form initial={initialForm} confirmLeave>
          {({ change }) => {
            const onPaymentChannelChange = createSingleAutocompleteSelectHandler(
              change,
              selectedValue => {
                setPaymentChannelDisplay(selectedValue);

                const arrayObject = paymentChoices.find(
                  arr => arr.label === selectedValue
                );
                setSelectedPaymentChannel(arrayObject?.value);
              },
              paymentChoices
            );

            return (
              <div className={classes.content}>
                <div className={classes.cardContainer}>
                  <div>
                    <Typography variant="subtitle1">Payment Channel</Typography>
                    <SingleAutocompleteSelectField
                      label={intl.formatMessage({
                        defaultMessage: "Select Payment Channel"
                      })}
                      choices={paymentChoices}
                      name="paymentChannel"
                      disabled={currentTab === 1}
                      displayValue={paymentChannelDisplay}
                      value={selectedPaymentChannel}
                      onChange={onPaymentChannelChange}
                      InputProps={{
                        autoComplete: "off"
                      }}
                    />
                  </div>
                </div>
                <CardSpacer />
                <Card>
                  <FilterTabs currentTab={currentTab}>
                    <FilterTab
                      onClick={() => tabChanged(0)}
                      label={intl.formatMessage({
                        defaultMessage: "For Remittance"
                      })}
                    />
                    <FilterTab
                      onClick={() => tabChanged(1)}
                      label={intl.formatMessage({
                        defaultMessage: "For Payment Collection"
                      })}
                    />
                  </FilterTabs>
                  <RemittanceRunnerTableToRemit
                    refetch={refetch}
                    handleCheckedAll={handleCheckedAll}
                    handleChecked={handleChecked}
                    checkedAll={checkedAll}
                    orderList={orderList}
                    checkedOrders={checkedOrders}
                    params={params}
                    setSearchQuery={setSearchQuery}
                    loadingOrders={loadingOrders}
                    disabled={false}
                    settings={settings}
                    onUpdateListSettings={updateListSettings}
                    pageInfo={pageInfoRemittance}
                    onPreviousPage={loadPreviousPageRemittance}
                    onRowClick={null}
                    onNextPage={loadNextPageRemittance}
                    forRemittance={currentTab === 0}
                    onFilterChange={e => {
                      if (params?.before || params?.after) {
                        delete params.before;
                        delete params.after;
                        navigate(
                          remittanceRunnerToRemitUrl({
                            ...params,
                            ...DEFAULT_INITIAL_PAGINATION_DATA
                          }),
                          true
                        );
                      }
                      changeFilters(e);
                    }}
                    filterOpts={filterOpts}
                    currencySymbol={""}
                  />
                </Card>

                <div className={classes.cardFooter}>
                  <div className={classes.remittedAmount}>
                    <Typography variant="subtitle1">
                      {currentTab === 0
                        ? "Total Order to be Remitted"
                        : "Total Order to be Collected"}
                    </Typography>
                    <Typography color="textPrimary" variant="h4">
                      <span>{currencyFormat(amountTotal)}</span>
                    </Typography>
                  </div>
                  <div className={classes.actions}>
                    {currentTab === 0 && (
                      <Typography variant="subtitle1" color="error">
                        {minRemittanceLimit > 0 && compareMinToCurrent
                          ? "Total remittance amount must be at least " +
                            currencyFormat(minRemittanceLimit, false)
                          : ""}
                      </Typography>
                    )}
                    <Button
                      variant="outlined"
                      className={[
                        classes.leftSpacer,
                        tblClasses.footerBtn,
                        "outlined"
                      ].join(" ")}
                      onClick={handleBack}
                    >
                      <FormattedMessage {...buttonMessages.cancel} />
                    </Button>
                    {currentTab === 1 ? (
                      <Button
                        disabled={markRemittanceAsPaymentCollectedOpts.loading}
                        variant="contained"
                        color="secondary"
                        className={tblClasses.footerBtn}
                        onClick={() => setDisplayDialog(true)}
                      >
                        <FormattedMessage {...buttonMessages.markAsCollected} />
                      </Button>
                    ) : (
                      <Button
                        disabled={
                          loadingOrders ||
                          generateRemittanceIdOpts.loading ||
                          (minRemittanceLimit > 0 && compareMinToCurrent)
                        }
                        variant="contained"
                        color="secondary"
                        className={tblClasses.footerBtn}
                        onClick={() => handleSave(false)}
                      >
                        <FormattedMessage {...buttonMessages.save} />
                      </Button>
                    )}
                    <RemittanceRunnerMarkAsCollectedModal
                      displayDialog={displayDialog}
                      onClose={() => setDisplayDialog(false)}
                      handleSave={() => handleSave(true)}
                    />
                  </div>
                </div>
              </div>
            );
          }}
        </Form>
      </Card>
    </Container>
  );
};

export default RemittanceRunnerToRemit;
